import { useParams } from 'react-router';
import { useSuspenseQuery } from '@apollo/client/react';
import { type ErrorLike } from '@apollo/client';
import { type TypedDocumentNode } from '@graphql-typed-document-node/core';

import { AppDocument, type IAppQuery } from 'src/graphql/actions/apps/app.query';
import {
  AppMembershipsDocument,
  type IAppMembershipsQuery,
  type IAppMembershipsQueryVariables,
} from 'src/graphql/actions/appMemberships/appMemberships.query';
import {
  AppSubscriptionsDocument,
  type IAppSubscriptionsQuery,
  type IAppSubscriptionsQueryVariables,
} from 'src/graphql/actions/appSubscriptions/appSubscriptions.query';

import { createRequiredContext } from '../createRequiredContext';

type IMembersCountQuery = IAppMembershipsQuery | IAppSubscriptionsQuery;
type IMembersCountQueryVariables = IAppMembershipsQueryVariables | IAppSubscriptionsQueryVariables;

function assertDataExists(error: ErrorLike | undefined, data: IAppQuery | undefined): asserts data is IAppQuery {
  if (error) throw error;
  if (!data?.app) throw new Error('App data not found');
}

const [useApp, AppProvider] = createRequiredContext<{
  app: NonNullable<IAppQuery['app']>;
  refetch: () => Promise<unknown>;
  membersCount: number;
}>();

const AppContextProvider = ({ children }: React.PropsWithChildren) => {
  const { appId = '' } = useParams();
  const { data, error, refetch } = useSuspenseQuery(AppDocument, {
    variables: { appId },
    skip: !appId,
  });

  assertDataExists(error, data);

  const membersDocument = (data.app.owned ? AppMembershipsDocument : AppSubscriptionsDocument) as TypedDocumentNode<
    IMembersCountQuery,
    IMembersCountQueryVariables
  >;
  const { data: membersData } = useSuspenseQuery(membersDocument, {
    variables: { appId, perPage: 0 },
  });
  const membersCount =
    'appMemberships' in membersData ? membersData.appMemberships.nodesCount : membersData.appSubscriptions.nodesCount;

  const providerValue = {
    app: data.app,
    refetch,
    membersCount,
  };

  return <AppProvider value={providerValue}>{children}</AppProvider>;
};

export { useApp, AppContextProvider, AppProvider };
